import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { useProfileComplete } from "@/hooks/useProfileComplete";
import { usePhoneVerified } from "@/hooks/usePhoneVerified";
import { lazy, Suspense } from "react";
import Layout from "@/components/Layout";
import { Loader2 } from "lucide-react";

const Auth = lazy(() => import("@/pages/Auth"));
const AssetDetail = lazy(() => import("@/pages/AssetDetail"));
const Management = lazy(() => import("@/pages/Management"));
const UserRoles = lazy(() => import("@/pages/UserRoles"));

const ActivityLog = lazy(() => import("@/pages/ActivityLog"));
const AuthLog = lazy(() => import("@/pages/AuthLog"));
const Assets = lazy(() => import("@/pages/Assets"));
const Future = lazy(() => import("@/pages/Future"));
const FutureDetail = lazy(() => import("@/pages/FutureDetail"));
const ProfileSetup = lazy(() => import("@/pages/ProfileSetup"));
const Profile = lazy(() => import("@/pages/Profile"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const VerifyPhone = lazy(() => import("@/pages/VerifyPhone"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Install = lazy(() => import("@/pages/Install"));
const UserProfileView = lazy(() => import("@/pages/UserProfileView"));
const Permissions = lazy(() => import("@/pages/Permissions"));
const Issues = lazy(() => import("@/pages/Issues"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const Terms = lazy(() => import("@/pages/Terms"));

const SuspenseLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="w-8 h-8 text-primary animate-spin" />
  </div>
);

const queryClient = new QueryClient();

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const { verified, loading: phoneLoading } = usePhoneVerified();
  const { isComplete, loading: profileLoading } = useProfileComplete();

  if (loading || phoneLoading || profileLoading) {
    return <SuspenseLoader />;
  }
  if (!user) return <Navigate to="/auth" replace />;
  if (verified === false) return <Navigate to="/verify-phone" replace />;
  if (isComplete === false) return <Navigate to="/profile-setup" replace />;
  return <Layout>{children}</Layout>;
};

const ProfileSetupRoute = () => {
  const { user, loading } = useAuth();
  const { verified, loading: phoneLoading } = usePhoneVerified();
  const { isComplete, loading: profileLoading } = useProfileComplete();

  if (loading || phoneLoading || profileLoading) {
    return <SuspenseLoader />;
  }
  if (!user) return <Navigate to="/auth" replace />;
  if (verified === false) return <Navigate to="/verify-phone" replace />;
  return <ProfileSetup />;
};

const VerifyPhoneRoute = () => {
  const { user, loading } = useAuth();
  const { verified, loading: phoneLoading } = usePhoneVerified();

  if (loading || phoneLoading) {
    return <SuspenseLoader />;
  }
  if (!user) return <Navigate to="/auth" replace />;
  if (verified) return <Navigate to="/" replace />;
  return <VerifyPhone />;
};

const AuthRoute = () => {
  const { user, loading } = useAuth();
  const { verified, loading: phoneLoading } = usePhoneVerified();
  const { landingPage, loading: permLoading } = usePermissions();
  if (loading || (user && (permLoading || phoneLoading))) {
    return <SuspenseLoader />;
  }
  if (user) {
    if (verified === false) return <Navigate to="/verify-phone" replace />;
    return <Navigate to={landingPage} replace />;
  }
  return <Auth />;
};

const RootRedirect = () => {
  const { user, loading } = useAuth();
  const { verified, loading: phoneLoading } = usePhoneVerified();
  const { landingPage, loading: permLoading } = usePermissions();
  if (loading || (user && (permLoading || phoneLoading))) {
    return <SuspenseLoader />;
  }
  if (!user) return <Navigate to="/auth" replace />;
  if (verified === false) return <Navigate to="/verify-phone" replace />;
  return <Navigate to={landingPage} replace />;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<SuspenseLoader />}>
            <Routes>
              <Route path="/auth" element={<AuthRoute />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-phone" element={<VerifyPhoneRoute />} />
              <Route path="/profile-setup" element={<ProfileSetupRoute />} />
              
              <Route path="/" element={<RootRedirect />} />
              <Route path="/asset/:displayId" element={<ProtectedRoute><AssetDetail /></ProtectedRoute>} />
              <Route path="/management" element={<ProtectedRoute><Management /></ProtectedRoute>} />
              <Route path="/activity" element={<ProtectedRoute><ActivityLog /></ProtectedRoute>} />
              <Route path="/auth-log" element={<ProtectedRoute><AuthLog /></ProtectedRoute>} />
              <Route path="/assets" element={<ProtectedRoute><Assets /></ProtectedRoute>} />
              <Route path="/future" element={<ProtectedRoute><Future /></ProtectedRoute>} />
              <Route path="/future/:id" element={<ProtectedRoute><FutureDetail /></ProtectedRoute>} />
              <Route path="/issues" element={<ProtectedRoute><Issues /></ProtectedRoute>} />
              <Route path="/roles" element={<ProtectedRoute><UserRoles /></ProtectedRoute>} />
              <Route path="/permissions" element={<ProtectedRoute><Permissions /></ProtectedRoute>} />
              <Route path="/user/:userId" element={<ProtectedRoute><UserProfileView /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/install" element={<Install />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
